import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { NavBar } from '../components/NavBar';
import { Card } from '../components/SubscriptionAppCard';
import { Toast } from '../components/Toast';
import { Info, ExternalLink, Clock } from 'lucide-react';
import { Button } from '../components/SubscriptionAppButton';
import { ModeChangeRequestModal } from '../components/ModeChangeRequestModal';
import { useAppMode } from '../contexts/AppModeContext';
export function SettingsGeneralPage() {
  const navigate = useNavigate();
  // Which mode WinePik runs in for this store (shared, app-wide)
  const {
    mode: usageMode,
    setMode: setUsageMode
  } = useAppMode();
  // Toggle states for discount settings
  const [disableDiscountConversion, setDisableDiscountConversion] = useState(false);
  // Switching between club and loyalty only happens with the WinePik team, so
  // the non-current card raises a request rather than changing anything here.
  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const [requestSent, setRequestSent] = useState(false);
  // Toast state
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const handleToggleChange = (setter: React.Dispatch<React.SetStateAction<boolean>>, value: boolean, settingName: string) => {
    setter(value);
    setToastMessage(`${settingName} ${value ? 'enabled' : 'disabled'}`);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };
  return <div className="bg-[#F1F5F9] min-h-full p-4 md:p-6" data-id="element-13154" data-mp="25u4hwy29tjmc">
      {/* NavBar */}
      <div className="mb-4 md:mb-6" data-id="element-13155" data-mp="2alvycfrpq8yr">
        <NavBar tabs={[]} title="General Settings" subtitle="Store information, sync preferences, and data settings" data-id="element-13156" data-mp="2amxn1n1nityy" />
      </div>

      {/* Main Content - Full Width */}
      <div className="space-y-4 md:space-y-6" data-id="element-13157" data-mp="2kld4eyavmov9">
        {/* Prototype-only state switcher, not part of the merchant experience */}
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center" data-id="element-13158" data-mp="2om6pzl0ujrkz">
          <span className="text-[13px] font-medium text-[#64748B]" data-id="element-13159" data-mp="phylt0zz5j81">
            Preview state:
          </span>
          <div className="inline-flex w-fit rounded-md border border-[#CBD5E1] bg-white p-1" role="group" aria-label="Prototype usage mode preview" data-id="element-13160" data-mp="kjnd4tlf1ere">
            <button type="button" onClick={() => setUsageMode('club-loyalty')} aria-pressed={usageMode === 'club-loyalty'} className={`h-[32px] px-3 rounded-[4px] text-[13px] font-medium transition-colors ${usageMode === 'club-loyalty' ? 'bg-[#E0F2FE] text-[#0EA5E9]' : 'text-[#64748B] hover:text-[#0F172A]'}`} data-id="element-13161" data-mp="tj0l1htw5va0">
              Wine club + loyalty
            </button>
            <button type="button" onClick={() => setUsageMode('loyalty-only')} aria-pressed={usageMode === 'loyalty-only'} className={`h-[32px] px-3 rounded-[4px] text-[13px] font-medium transition-colors ${usageMode === 'loyalty-only' ? 'bg-[#E0F2FE] text-[#0EA5E9]' : 'text-[#64748B] hover:text-[#0F172A]'}`} data-id="element-13162" data-mp="1h68qq0v8f6ck">
              Loyalty only
            </button>
          </div>
          <span className="text-[12px] text-[#94A3B8]" data-id="element-13163" data-mp="34vh8rv4r6dwk">Prototype control</span>
        </div>

        {/* Section 0: Usage Mode */}
        <Card padding="none" data-id="element-13164" data-mp="38xlxqgk9amcv">
          <div className="p-4 md:p-6 border-b border-[#E2E8F0]" data-id="element-13165" data-mp="2v4wa67p2x9dn">
            <h2 className="text-[18px] md:text-[20px] font-semibold text-[#0F172A]" data-id="element-13166" data-mp="2v2jvc3embf4t">
              What you're using WinePik for
            </h2>
            <p className="text-[13px] md:text-[14px] text-[#64748B] mt-1" data-id="element-13167" data-mp="2l5x8xa6n8g0o">
              Choose how WinePik runs for your store. You can change this later.
            </p>
          </div>

          <div className="p-4 md:p-6" data-id="element-13168" data-mp="2c61soqy8duts">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4" data-id="element-13169" data-mp="2c73817ma53hb">
              {[{
              id: 'club-loyalty' as const,
              title: 'Wine club + loyalty',
              description: 'Run subscription wine clubs with loyalty and rewards. The full WinePik toolset.'
            }, {
              id: 'loyalty-only' as const,
              title: 'Loyalty only',
              description: 'Run a loyalty and rewards program on your Shopify store, with no subscriptions. Points are earned on regular Shopify orders.'
            }].map(option => {
              const selected = usageMode === option.id;
              return <div key={option.id} className={`text-left rounded-lg border p-4 transition-all duration-200 ${selected ? 'border-[#0EA5E9] bg-[#F0F9FF] ring-1 ring-[#0EA5E9]' : 'border-[#CBD5E1] bg-white'}`} data-id="element-13170" data-mp="1xglvc4hlt4x7">
                    <div className="flex items-start gap-3" data-id="element-13171" data-mp="1nr29cpylguy4">
                      <div className={`mt-0.5 w-4 h-4 rounded-full border flex items-center justify-center flex-shrink-0 ${selected ? 'border-[#0EA5E9]' : 'border-[#CBD5E1]'}`} data-id="element-13172" data-mp="1nopkdsuf5kbk">
                        {selected && <div className="w-2 h-2 rounded-full bg-[#0EA5E9]" data-id="element-13173" data-mp="1dufr88xnkhzs" />}
                      </div>
                      <div data-id="element-13174" data-mp="19t3znxv7xmoy">
                        <h3 className="text-[15px] md:text-[16px] font-semibold text-[#0F172A]" data-id="element-13175" data-mp="14s6j1ppux0pk">
                          {option.title}
                        </h3>
                        <p className="text-[13px] md:text-[14px] text-[#64748B] mt-1" data-id="element-13176" data-mp="3gf0710mbx05c">
                          {option.description}
                        </p>

                        {!selected && <div className="mt-3" data-id="element-13177" data-mp="3ph9j3ux8x1ku">
                            {requestSent ? <span className="inline-flex items-center gap-2 text-[13px] font-medium text-[#64748B]" data-id="element-13178" data-mp="3ufkuia36vk56">
                                <Clock className="w-4 h-4 text-[#94A3B8]" data-id="element-13179" data-mp="2xptmd81z5ygw" />
                                Request sent, we'll be in touch
                              </span> : <Button variant="secondary" onClick={() => setRequestModalOpen(true)} data-id="element-13180" data-mp="31o14o7c377u7">
                                {option.id === 'loyalty-only' ? 'Talk to us about switching' : 'Talk to us about adding a club'}
                              </Button>}
                          </div>}
                      </div>
                    </div>
                  </div>;
            })}
            </div>

            <p className="text-[12px] md:text-[13px] text-[#94A3B8] mt-4" data-id="element-13181" data-mp="2ixji6khul8ha">
              Switching between wine club and loyalty only is something we do
              with you rather than instantly. Nothing is deleted either way, and
              your existing members and subscriptions are unaffected until the
              switch is made.
            </p>
          </div>
        </Card>

        {/* Section 1: Store Information */}
        <Card padding="none" data-id="element-13182" data-mp="33b5u6z214gxl">
          <div className="p-4 md:p-6 border-b border-[#E2E8F0] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3" data-id="element-13183" data-mp="33ch6xodgcz0u">
            <div data-id="element-13184" data-mp="3ccm1gxxjs2xl">
              <h2 className="text-[18px] md:text-[20px] font-semibold text-[#0F172A]" data-id="element-13185" data-mp="3cd5ec08cc14r">
                Store Information
              </h2>
              <p className="text-[13px] md:text-[14px] text-[#64748B] mt-1" data-id="element-13186" data-mp="37bzkfz2y27tn">
                Synced from your Shopify store
              </p>
            </div>
            <a href="https://admin.shopify.com/settings" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-[13px] md:text-[14px] font-medium text-[#0EA5E9] hover:text-[#0284C7] transition-colors" data-id="element-13187" data-mp="mntnti7gj75d">
              Edit in Shopify
              <ExternalLink className="w-4 h-4" data-id="element-13188" data-mp="z3ky2086ak1h" />
            </a>
          </div>

          <div className="p-4 md:p-6" data-id="element-13189" data-mp="l7zcy4528kec">
            {/* Info Banner */}
            <div className="mb-4 md:mb-6 p-3 md:p-4 bg-[#E0F2FE] border border-[#7DD3FC] rounded-md flex items-start gap-3" data-id="element-13190" data-mp="q60rqcvgdxk0">
              <Info className="w-5 h-5 text-[#0369A1] flex-shrink-0 mt-0.5" data-id="element-13191" data-mp="1dvlllqt5af82" />
              <p className="text-[13px] md:text-[14px] text-[#0369A1]" data-id="element-13192" data-mp="1islm7m786prs">
                These settings are automatically synced from Shopify. To make
                changes, update your Shopify store settings.
              </p>
            </div>

            {/* Info Rows */}
            <div className="space-y-3 md:space-y-4" data-id="element-13193" data-mp="1wtedzz6576r7">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between py-3 border-b border-[#E2E8F0] gap-2" data-id="element-13194" data-mp="1wruywbw831r0">
                <span className="text-[13px] md:text-[14px] text-[#64748B] font-medium" data-id="element-13195" data-mp="1iesh8t0s1ik">
                  Store Name
                </span>
                <span className="text-[15px] md:text-[16px] text-[#0F172A] font-medium text-left sm:text-right" data-id="element-13196" data-mp="3svhwbfvsram5">
                  Acme Wine Co
                </span>
              </div>

              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between py-3 border-b border-[#E2E8F0] gap-2" data-id="element-13197" data-mp="3jxpdi1qv9ku0">
                <span className="text-[13px] md:text-[14px] text-[#64748B] font-medium" data-id="element-13198" data-mp="fhwmq3p346zb">
                  Store Email
                </span>
                <span className="text-[15px] md:text-[16px] text-[#0F172A] font-medium text-left sm:text-right" data-id="element-13199" data-mp="bf9sxx8usbzh">
                  orders@acmewine.com
                </span>
              </div>

              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between py-3 border-b border-[#E2E8F0] gap-2" data-id="element-13200" data-mp="2w7lanwli5p42">
                <span className="text-[13px] md:text-[14px] text-[#64748B] font-medium" data-id="element-13201" data-mp="2r9jrzcrznrt4">
                  Store Address
                </span>
                <span className="text-[15px] md:text-[16px] text-[#0F172A] font-medium text-left sm:text-right max-w-md" data-id="element-13202" data-mp="1bnzpo9q4h1dw">
                  123 Vineyard Lane, Napa Valley, CA 94558
                </span>
              </div>

              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between py-3 border-b border-[#E2E8F0] gap-2" data-id="element-13203" data-mp="1khmips2y3qje">
                <span className="text-[13px] md:text-[14px] text-[#64748B] font-medium" data-id="element-13204" data-mp="1pia5u2o1zn8c">
                  Currency
                </span>
                <span className="text-[15px] md:text-[16px] text-[#0F172A] font-medium text-left sm:text-right" data-id="element-13205" data-mp="1zdtzsjj24bug">
                  AUD - Australian Dollar
                </span>
              </div>

              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between py-3 gap-2" data-id="element-13206" data-mp="50ufwe7n8vhd">
                <span className="text-[13px] md:text-[14px] text-[#64748B] font-medium" data-id="element-13207" data-mp="92pdp8qd8qqe">
                  Timezone
                </span>
                <span className="text-[15px] md:text-[16px] text-[#0F172A] font-medium text-left sm:text-right" data-id="element-13208" data-mp="3jfyllwudrx9p">
                  Australia/Melbourne (AEST)
                </span>
              </div>
            </div>
          </div>
        </Card>

        {/* Section 2: Discount Behavior */}
        <Card padding="none" data-id="element-13209" data-mp="1662yizz720p">
          <div className="p-4 md:p-6 border-b border-[#E2E8F0]" data-id="element-13210" data-mp="67lo0b25l8j3">
            <h2 className="text-[18px] md:text-[20px] font-semibold text-[#0F172A]" data-id="element-13211" data-mp="673maywq223s">
              Discount Settings
            </h2>
            <p className="text-[13px] md:text-[14px] text-[#64748B] mt-1" data-id="element-13212" data-mp="ezxfm090720n">
              Configure how discounts work with subscriptions
            </p>
          </div>

          <div className="p-4 md:p-6" data-id="element-13213" data-mp="k0ut3zj2j7vt">
            <div className="flex items-start justify-between py-3 md:py-4 gap-4" data-id="element-13214" data-mp="oyd55ysdu381">
              <div className="flex-1 pr-4 md:pr-6" data-id="element-13215" data-mp="twxxzd5b1kmg">
                <h3 className="text-[15px] md:text-[16px] font-semibold text-[#0F172A] mb-1" data-id="element-13216" data-mp="txfzao6tqp18">
                  Disable Automatic Discount Conversion
                </h3>
                <p className="text-[13px] md:text-[14px] text-[#64748B]" data-id="element-13217" data-mp="29fmaxg9e2uou">
                  Prevent one-time discount codes from automatically converting
                  to subscription discounts
                </p>
              </div>
              <button onClick={() => handleToggleChange(setDisableDiscountConversion, !disableDiscountConversion, 'Automatic Discount Conversion')} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-offset-2 flex-shrink-0 ${disableDiscountConversion ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} role="switch" aria-checked={disableDiscountConversion} data-id="element-13218" data-mp="1zjj07m0h1nqa">
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${disableDiscountConversion ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-13219" data-mp="3fvod9d4hjb6t" />
              </button>
            </div>
          </div>
        </Card>
      </div>

      {/* Toast Notification */}
      {showToast && <div className="fixed bottom-6 right-6 z-50 animate-in slide-in-from-bottom-2 duration-200" data-id="element-13220" data-mp="1e7achu3x60v9">
          <div className="bg-white border border-[#CBD5E1] rounded-lg shadow-lg px-4 py-3 flex items-center gap-3 min-w-[320px]" data-id="element-13221" data-mp="zie8c84zh7zx">
            <div className="w-1 h-10 bg-[#10B981] rounded-full" data-id="element-13222" data-mp="14fo2g13bi7u3" />
            <div className="flex-1" data-id="element-13223" data-mp="qgoe6ujpvuyg">
              <p className="text-[14px] font-semibold text-[#0F172A]" data-id="element-13224" data-mp="qfmslw52rq2h">
                Settings saved
              </p>
              <p className="text-[13px] text-[#64748B]" data-id="element-13225" data-mp="ugoukkqdmg85">{toastMessage}</p>
            </div>
          </div>
        </div>}

      {requestModalOpen && <ModeChangeRequestModal direction={usageMode === 'club-loyalty' ? 'to-loyalty-only' : 'add-club'} title={usageMode === 'club-loyalty' ? 'Switch to loyalty only' : 'Talk to us about adding a club'} defaultWineryName="Acme Wine Co" defaultContactEmail="orders@acmewine.com" onClose={() => setRequestModalOpen(false)} onSubmitted={() => setRequestSent(true)} data-id="element-13226" data-mp="lhlabdfh3us2" />}
    </div>;
}